import React from "react";
import { RemoveCircle } from "@mui/icons-material";
import { Alert, AlertTitle } from "@mui/material";

import { Progress } from "ui";
import { StepStatus } from "ui/Progress/Progress";

import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { useAppSelector } from "fond/utils/hooks";
import { BlockSpinner, StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";

// The workflow has many steps, but we only show 3 in the UI.
const combinedWorkflowSteps = {
  inputs: [0, 1, 2, 3],
  counting: [4],
  calculate: [5, 6],
};

interface DataModificationPanelProps {
  // Flag indicating if the editing is read only
  isReadOnly?: boolean;
}

const DataModificationPanel: React.FC<DataModificationPanelProps> = ({ isReadOnly }) => {
  const { open } = useStackedNavigationContext();
  const versionId = useAppSelector((state) => state.project.versionId);
  const { workflowState, workflowStepIndex, isLoading, runWorkflow } = useCustomWorkflow({ versionId, key: "nbn_cost_to_serve_iteration" });
  const isGeneratingCTS = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  /**
   * Determines the status of a step based on the workflow step index and state.
   */
  const getStatus = (steps: number[]): StepStatus => {
    if (workflowStepIndex == null) return "Idle";
    if (Math.max(...steps) < workflowStepIndex) return "Success";
    if (steps.includes(workflowStepIndex) && workflowState === "ERROR") return "Error";
    if (steps.includes(workflowStepIndex)) return "InProgress";
    if (workflowState !== "ERROR") return "Idle";

    return "NotReady";
  };

  if (isLoading) return <BlockSpinner />;

  return (
    <>
      {workflowState === undefined && (
        <StackedNavigationButton
          data-testid="data-modiciation-navigation"
          icon={<RemoveCircle />}
          title="Exclude service locations"
          onClick={() => {
            open("excludeDraw");
          }}
          sx={{ m: 0, mt: 1 }}
          disabled={isReadOnly}
        />
      )}

      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
          Cost to serve generated.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Cost to serve generation failed</AlertTitle>
          Cost to serve could not be generated.
        </Alert>
      )}

      {(isGeneratingCTS || workflowState === "ERROR") && (
        <Progress
          steps={[
            { ID: "inputs", Label: "Preparing inputs", Status: getStatus(combinedWorkflowSteps.inputs) },
            {
              ID: "counting",
              Label: "Counting materials and costs",
              Status: getStatus(combinedWorkflowSteps.counting),
            },
            {
              ID: "calculate",
              Label: "Calculating cost to serve",
              Status: getStatus(combinedWorkflowSteps.calculate),
            },
          ]}
        />
      )}
    </>
  );
};

export default DataModificationPanel;
